import React, { useMemo, useState } from 'react';

import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { Box, Button, Chip, Stack, Tooltip, Typography } from '@mui/material';

import { getRegionNamesAndCode } from '@luxuryescapes/lib-regions';

import SetRegionModal from '~/components/Common/Modals/SetRegionModal';

const VISIBLE_REGION_COUNT = 5;

interface Props {
  offerPackage: App.Package;
  onChange: (nextPackageValues: Partial<App.Package> & Record<string, unknown>) => void;
}

function getPackageRegions(offerPackage: App.Package) {
  return Array.isArray(offerPackage.regions) ? offerPackage.regions : ['world'];
}

export default function PackageRegionsTab({ offerPackage, onChange }: Props) {
  const [isRegionsModalOpen, setIsRegionsModalOpen] = useState(false);
  const selectedRegions = getPackageRegions(offerPackage);
  const regionsByCode = useMemo(() => new Map(getRegionNamesAndCode().map((region) => [region.code, region.name])), []);
  const regionNames = useMemo(() => {
    if (selectedRegions.includes('world')) {
      return Array.from(regionsByCode.values());
    }

    return selectedRegions.map((region) => regionsByCode.get(region) ?? region);
  }, [regionsByCode, selectedRegions]);
  const visibleRegions = regionNames.slice(0, VISIBLE_REGION_COUNT);
  const remainingRegions = regionNames.slice(VISIBLE_REGION_COUNT);

  const handleSetRegions = (regions: Array<string>) => {
    onChange({ regions });
    setIsRegionsModalOpen(false);
  };

  return (
    <>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
        <Typography variant="body1" color="text.secondary">
          Tag regions where this package should be visible.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<PublicOutlinedIcon />}
          onClick={() => setIsRegionsModalOpen(true)}
          sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
        >
          Set regions
        </Button>
      </Stack>

      <Box sx={{ mt: 4 }}>
        {regionNames.length > 0 ? (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {visibleRegions.map((region) => (
              <Chip key={region} label={region} variant="outlined" />
            ))}
            {remainingRegions.length > 0 && (
              <Tooltip title={remainingRegions.join(', ')}>
                <Chip label={`+ ${remainingRegions.length} more`} variant="outlined" />
              </Tooltip>
            )}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No regions selected.
          </Typography>
        )}
      </Box>

      <SetRegionModal
        open={isRegionsModalOpen}
        onRequestClose={() => setIsRegionsModalOpen(false)}
        onSet={handleSetRegions}
        regions={selectedRegions}
        name="Package Regions"
        label="Tag all regions that you want this package to be visible in"
      />
    </>
  );
}
